if (!customElements.get('site-lock')) {
  class SiteLock extends HTMLElement {
    constructor() {
      super();

      this.dismissAllowed = false;
      this.pageScrollLocked = false;
      this.onDialogCancel = this.onDialogCancel.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.onDialogKeydown = this.onDialogKeydown.bind(this);
      this.onShopifyFormSubmit = this.onShopifyFormSubmit.bind(this);
    }

    getStoredValue(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn('Site Lock: localStorage not available', e);
        return null;
      }
    }

    setStoredValue(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        console.warn('Site Lock: localStorage not available', e);
      }
    }

    connectedCallback() {
      this.dialog = this.querySelector('dialog');

      if (!this.dialog) {
        return;
      }

      this.dialog.addEventListener('cancel', this.onDialogCancel);
      this.dialog.addEventListener('close', this.onDialogClose);
      this.dialog.addEventListener('keydown', this.onDialogKeydown);

      this.isDesignMode =
        this.hasAttribute('data-design-mode') ||
        (typeof window.Shopify !== 'undefined' && window.Shopify.designMode);

      this.isKlaviyoForm = !!this.querySelector('[data-form-type="klaviyo"]');

      if (this.isKlaviyoForm) {
        this.setupKlaviyoForm();
      } else {
        this.form = this.querySelector('#site-lock-form');
        this.emailInput = this.querySelector('#SiteLockForm-email');
      }

      this.countdownElement = this.querySelector('.site-lock__countdown-value');
      this.countdownTimeElement = this.querySelector('.site-lock__countdown-time');

      if (this.isDesignMode) {
        return;
      }

      if (!this.isWithinTimeWindow()) {
        return;
      }

      const formSuccess = this.querySelector('[data-form-success="true"]');

      if (formSuccess) {
        this.setStoredValue('site-lock-submitted', 'true');
        return;
      }

      const hasSubmitted = this.getStoredValue('site-lock-submitted');

      if (hasSubmitted) {
        return;
      }

      this.initializeLock();
    }

    editorOpen() {
      this.classList.add('site-lock--editor-selected');
      this.showLock();

      if (this.countdownElement) {
        this.updateCountdown();
      }
    }

    editorClose() {
      this.classList.remove('site-lock--editor-selected');
      this.hideLock();
    }

    shouldKeepDialogOpen() {
      if (this.isDesignMode) {
        return this.classList.contains('site-lock--editor-selected');
      }

      return true;
    }

    initializeLock() {
      this.showLock();
      this.startTimeWindowMonitor();

      if (!this.isKlaviyoForm && this.form) {
        this.form.addEventListener('submit', this.onShopifyFormSubmit);
      }
    }

    isWithinTimeWindow() {
      const startTimeStr = this.getAttribute('data-start-time');
      const endTimeStr = this.getAttribute('data-end-time');

      if (!startTimeStr && !endTimeStr) {
        return true;
      }

      const nowTimestamp = Math.floor(Date.now() / 1000);

      if (startTimeStr) {
        const startTimestamp = parseInt(startTimeStr, 10);

        if (nowTimestamp < startTimestamp) {
          return false;
        }
      }

      if (endTimeStr) {
        const endTimestamp = parseInt(endTimeStr, 10);

        if (nowTimestamp > endTimestamp) {
          return false;
        }
      }

      return true;
    }

    startTimeWindowMonitor() {
      const startTimeStr = this.getAttribute('data-start-time');
      const endTimeStr = this.getAttribute('data-end-time');

      if (!startTimeStr && !endTimeStr) {
        return;
      }

      this.updateCountdown();

      const intervalTime = this.countdownElement ? 1000 : 10000;

      this.timeWindowInterval = setInterval(() => {
        if (this.countdownElement) {
          this.updateCountdown();
        }

        if (!this.isWithinTimeWindow() && !this.isDesignMode) {
          this.hideLock();
          this.stopTimeWindowMonitor();
        }
      }, intervalTime);
    }

    updateCountdown() {
      if (!this.countdownElement) {
        return;
      }

      const endTimeStr = this.getAttribute('data-end-time');

      if (!endTimeStr) {
        return;
      }

      const endTimestamp = parseInt(endTimeStr, 10);
      const nowTimestamp = Math.floor(Date.now() / 1000);
      const remainingSeconds = endTimestamp - nowTimestamp;

      if (remainingSeconds <= 0) {
        this.countdownElement.textContent = 'Expired';
        return;
      }

      const days = Math.floor(remainingSeconds / 86400);
      const hours = Math.floor((remainingSeconds % 86400) / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      const seconds = remainingSeconds % 60;

      let countdownText = '';

      if (days > 0) {
        countdownText += `${days}d `;
      }

      if (hours > 0 || days > 0) {
        countdownText += `${hours}h `;
      }

      if (minutes > 0 || hours > 0 || days > 0) {
        countdownText += `${minutes}m `;
      }

      countdownText += `${seconds}s`;

      this.countdownElement.textContent = countdownText.trim();

      if (this.countdownTimeElement) {
        const endDate = new Date(endTimestamp * 1000);
        this.countdownTimeElement.setAttribute('datetime', endDate.toISOString());
      }
    }

    stopTimeWindowMonitor() {
      if (this.timeWindowInterval) {
        clearInterval(this.timeWindowInterval);
        this.timeWindowInterval = null;
      }
    }

    disconnectedCallback() {
      this.stopTimeWindowMonitor();

      if (this.dialog) {
        this.dialog.removeEventListener('cancel', this.onDialogCancel);
        this.dialog.removeEventListener('close', this.onDialogClose);
        this.dialog.removeEventListener('keydown', this.onDialogKeydown);

        if (this.dialog.open) {
          this.closeDialog();
          this.unlockPageScroll();
        }
      }

    }

    setupKlaviyoForm() {
      const klaviyoHandler = (e) => {
        if (e.detail.type !== 'submit') {
          return;
        }

        if (this.getStoredValue('site-lock-submitted')) {
          return;
        }

        this.setStoredValue('site-lock-submitted', 'true');
        this.hideLock();
      };

      window.addEventListener('klaviyoForms', klaviyoHandler);
    }

    showLock() {
      if (!this.dialog || this.dialog.open) {
        return;
      }

      this.dialog.showModal();
      this.lockPageScroll();
    }

    lockPageScroll() {
      if (this.pageScrollLocked) {
        return;
      }

      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
      this.pageScrollLocked = true;
    }

    unlockPageScroll() {
      if (!this.pageScrollLocked) {
        return;
      }

      this.pageScrollLocked = false;

      if (typeof window.theme?.hasOpenModals === 'function' && window.theme.hasOpenModals()) {
        return;
      }

      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }

    closeDialog() {
      if (!this.dialog) {
        return;
      }

      this.dismissAllowed = true;
      this.dialog.close();

      queueMicrotask(() => {
        this.dismissAllowed = false;
      });
    }

    hideLock() {
      if (!this.dialog || !this.dialog.open) {
        return;
      }

      this.closeDialog();
      this.unlockPageScroll();
      this.stopTimeWindowMonitor();
    }

    onDialogCancel(event) {
      if (!this.dismissAllowed) {
        event.preventDefault();
      }
    }

    onDialogClose() {
      if (this.dismissAllowed || !this.dialog) {
        return;
      }

      requestAnimationFrame(() => {
        if (this.dismissAllowed || !this.dialog || this.dialog.open) {
          return;
        }

        if (!this.shouldKeepDialogOpen()) {
          return;
        }

        this.dialog.showModal();
      });
    }

    onDialogKeydown(event) {
      if (!this.dismissAllowed && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    onShopifyFormSubmit(event) {
      const email = this.emailInput.value.trim();

      if (!email || !this.isValidEmail(email)) {
        event.preventDefault();
        this.emailInput.focus();
      }
    }

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }
  }

  customElements.define('site-lock', SiteLock);
}
